import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Hierarchical checkbox controller for managing checkbox trees
// Used in developer panel for schema export field selection
export default class extends Controller {
  static targets = ["master", "schemaCheckbox", "groupCheckbox", "fieldCheckbox"]

  connect() {
    logger.log("Hierarchical checkbox controller connected")

    // Update all group checkbox states first (if present)
    if (this.hasGroupCheckboxTarget) {
      this.groupCheckboxTargets.forEach(groupCheckbox => {
        const groupId = groupCheckbox.dataset.groupId

        // Find all fields in this group
        const groupFields = this.fieldCheckboxTargets.filter(
          checkbox => checkbox.dataset.groupId === groupId
        )

        const checkedFields = groupFields.filter(field => field.checked)

        // Update group checkbox state based on field states
        // Simple 2-state: checked if ANY field is checked, unchecked if NONE
        if (checkedFields.length === 0) {
          groupCheckbox.checked = false
        } else {
          groupCheckbox.checked = true
        }
      })
    }

    // Update all schema checkbox states (if present)
    if (this.hasSchemaCheckboxTarget) {
      this.schemaCheckboxTargets.forEach(schemaCheckbox => {
        const schemaId = schemaCheckbox.dataset.schemaId
        this.updateSchemaStateInternal(schemaId)
      })
    }

    // Update master state
    this.updateMasterState()
  }

  // Toggle all checkboxes when master checkbox is clicked
  toggleAll(event) {
    const isChecked = event.target.checked
    logger.log(`Toggle all: ${isChecked}`)

    // Toggle all schema checkboxes (if present)
    if (this.hasSchemaCheckboxTarget) {
      this.schemaCheckboxTargets.forEach(checkbox => {
        checkbox.checked = isChecked
      })
    }

    // Toggle all group checkboxes (if present)
    if (this.hasGroupCheckboxTarget) {
      this.groupCheckboxTargets.forEach(checkbox => {
        checkbox.checked = isChecked
      })
    }

    // Toggle all field checkboxes
    this.fieldCheckboxTargets.forEach(checkbox => {
      checkbox.checked = isChecked
      // Dispatch change event to trigger any bound actions (like autoSelectReport)
      if (isChecked) {
        checkbox.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  // Toggle all groups and fields in a schema when schema checkbox is clicked
  toggleSchema(event) {
    const schemaCheckbox = event.target
    const schemaId = schemaCheckbox.dataset.schemaId
    const isChecked = schemaCheckbox.checked

    logger.log(`Toggle schema ${schemaId}: ${isChecked}`)

    // Find all groups belonging to this schema
    const schemaGroups = this.groupCheckboxTargets.filter(
      checkbox => checkbox.dataset.schemaId === schemaId
    )

    // Find all fields belonging to this schema
    const schemaFields = this.fieldCheckboxTargets.filter(
      checkbox => checkbox.dataset.schemaId === schemaId
    )

    // Toggle all groups in the schema
    schemaGroups.forEach(group => {
      group.checked = isChecked
    })

    // Toggle all fields in the schema
    schemaFields.forEach(field => {
      field.checked = isChecked
      // Dispatch change event to trigger any bound actions (like autoSelectReport)
      if (isChecked) {
        field.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    // Update master checkbox state
    this.updateMasterState()
  }

  // Toggle all fields in a group when group checkbox is clicked
  toggleGroup(event) {
    const groupCheckbox = event.target
    const groupId = groupCheckbox.dataset.groupId
    const schemaId = groupCheckbox.dataset.schemaId
    const isChecked = groupCheckbox.checked

    logger.log(`Toggle group ${groupId}: ${isChecked}`)

    // Find all fields belonging to this group
    const groupFields = this.fieldCheckboxTargets.filter(
      checkbox => checkbox.dataset.groupId === groupId
    )

    // Toggle all fields in the group
    groupFields.forEach(field => {
      field.checked = isChecked
      // Dispatch change event to trigger any bound actions (like autoSelectReport)
      if (isChecked) {
        field.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    // Update schema checkbox if this group belongs to a schema
    if (schemaId) {
      this.updateSchemaStateInternal(schemaId)
    }

    // Update master checkbox state
    this.updateMasterState()
  }

  // Update group checkbox state when a field is toggled
  updateGroupState(event) {
    const fieldCheckbox = event.target
    const groupId = fieldCheckbox.dataset.groupId
    const schemaId = fieldCheckbox.dataset.schemaId

    logger.log(`Update group ${groupId} state`)

    // Find the group checkbox
    const groupCheckbox = this.groupCheckboxTargets.find(
      checkbox => checkbox.dataset.groupId === groupId
    )

    if (groupCheckbox) {
      // Find all fields in this group
      const groupFields = this.fieldCheckboxTargets.filter(
        checkbox => checkbox.dataset.groupId === groupId
      )

      const checkedFields = groupFields.filter(field => field.checked)

      // Update group checkbox state based on field states
      // Simple 2-state: checked if ANY field is checked, unchecked if NONE
      if (checkedFields.length === 0) {
        groupCheckbox.checked = false
      } else {
        groupCheckbox.checked = true
      }
    }

    // Update schema checkbox if this field belongs to a schema
    if (schemaId) {
      this.updateSchemaStateInternal(schemaId)
    }

    // Update master checkbox state
    this.updateMasterState()
  }

  // Update master checkbox state based on all field checkboxes (the actual selections)
  updateMasterState() {
    if (!this.hasMasterTarget) return
    if (!this.hasFieldCheckboxTarget) return

    const masterCheckbox = this.masterTarget

    // Count all field checkboxes (these are the actual items being selected)
    const totalFields = this.fieldCheckboxTargets.length
    if (totalFields === 0) return

    const checkedFields = this.fieldCheckboxTargets.filter(cb => cb.checked).length

    // Update master checkbox state based on field states
    if (checkedFields === 0) {
      // No fields checked
      masterCheckbox.checked = false
      masterCheckbox.indeterminate = false
    } else if (checkedFields === totalFields) {
      // All fields checked
      masterCheckbox.checked = true
      masterCheckbox.indeterminate = false
    } else {
      // Some fields checked
      masterCheckbox.checked = false
      masterCheckbox.indeterminate = true
    }

    logger.log(`Master state: checked=${masterCheckbox.checked}, indeterminate=${masterCheckbox.indeterminate}, fields=${checkedFields}/${totalFields}`)
  }

  // Update schema checkbox state (internal method - doesn't trigger events)
  updateSchemaStateInternal(schemaId) {
    if (!this.hasSchemaCheckboxTarget) return

    logger.log(`Update schema ${schemaId} state`)

    // Find the schema checkbox
    const schemaCheckbox = this.schemaCheckboxTargets.find(
      checkbox => checkbox.dataset.schemaId === schemaId
    )

    if (!schemaCheckbox) return

    // Find all fields belonging to this schema
    const schemaFields = this.fieldCheckboxTargets.filter(
      checkbox => checkbox.dataset.schemaId === schemaId
    )

    if (schemaFields.length === 0) return

    const checkedFields = schemaFields.filter(field => field.checked)

    // Update schema checkbox state based on field states
    // Simple 2-state: checked if ANY field is checked, unchecked if NONE
    if (checkedFields.length === 0) {
      schemaCheckbox.checked = false
    } else {
      schemaCheckbox.checked = true
    }

    logger.log(`Schema ${schemaId} state: checked=${schemaCheckbox.checked}, fields=${checkedFields.length}/${schemaFields.length}`)
  }
}
